import React from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";

interface CustomProgressProps {
  filledValue: number; // actual filled value
  total: number; // total value
  variant?: "determinate" | "indeterminate";
  height?: number;
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  filledColor?: string;
  unfilledColor?: string;
}

const CustomProgress: React.FC<CustomProgressProps> = ({
  filledValue,
  total,
  variant = "determinate",
  height = 12,
  color = "warning",
  filledColor,
  unfilledColor,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const unfilledValue = Math.max(0, total - filledValue);
  const filled = total > 0 ? (filledValue / total) * 100 : 0;
  const unfilled = 100 - filled;

  const formatPercentage = (value: number) => {
    const rounded = parseFloat(value.toFixed(2));
    return Number.isInteger(rounded) ? `${rounded.toFixed(0)}%` : `${rounded}%`;
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height,
          borderRadius: "999px",
          backgroundColor: unfilledColor || colors.grey[900],
          overflow: "visible",
          boxShadow: `0 2px 5px ${colors.grey[900]}33`,
        }}
      >
        {/* Filled Area Tooltip */}
        <Tooltip
          title={`Paid: $${filledValue.toFixed(2)}`}
          placement="top"
          arrow
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${Math.min(filled, 100)}%`,
              background:
                filledColor ||
                `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
              borderRadius: "999px",
              zIndex: 2,
              pointerEvents: "auto",
              transition: "width 0.3s ease-in-out",
            }}
          />
        </Tooltip>

        {filled < 100 && (
          <Tooltip
            title={`Remaining: $${unfilledValue.toFixed(2)}`}
            placement="top"
            arrow
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: `${filled}%`,
                height: "100%",
                width: `${unfilled}%`,
                borderRadius: "0 999px 999px 0",
                zIndex: 1,
                pointerEvents: "auto",
              }}
            />
          </Tooltip>
        )}
      </Box>

      <Typography
        variant="body2"
        color="text.primary"
        sx={{
          minWidth: 48,
          fontWeight: 600,
          fontSize: "0.875rem",
          textAlign: "right",
        }}
      >
        {formatPercentage(filled)}
      </Typography>
    </Box>
  );
};

export default CustomProgress;
